'use client'

import { FC, Fragment } from 'react'
import { testIds } from '@/root/shared-consts'
import { useGetTranslations, useAppConfig } from '@/root/shared-hooks'
import { TicketsIcon } from '@/root/ui/shared/icons/TicketsIcon'
import { Reservation } from '@/root/shared-types'
import { getCartSummary } from '@/root/modules/shared/SelectTicketPage/helpers/getCartSummary/getCartSummary'

interface CartCardProps {
  reservation?: Reservation
}

export const CartCard: FC<CartCardProps> = ({ reservation }) => {
  const { getTranslation } = useGetTranslations()
  const { formatMoney } = useAppConfig()

  const { items, totalPrice, isEmpty } = getCartSummary(reservation)

  return (
    <div data-testid={testIds.tickets.cartCard} className="flex w-full flex-col gap-3">
      <div className="flex items-center gap-2">
        <TicketsIcon width={20} height={24} className="shrink-0" color="neutral-darkest" />
        <h2 className="text-neutral-darkest text-2xl leading-none font-bold">
          {getTranslation('selectedTicketsCartTitle')}
        </h2>
      </div>

      {isEmpty ? (
        <p className="text-neutral-darkest text-base leading-none">{getTranslation('selectedTicketsCartEmpty')}</p>
      ) : (
        <>
          <div className="grid grid-cols-[1fr_auto_auto] items-start gap-x-4 gap-y-3">
            {items.map((item) => (
              <Fragment key={item.priceCategory}>
                <span
                  data-testid={testIds.tickets.cartItemCategory}
                  className="text-neutral-darkest text-base leading-none"
                >
                  {item.priceCategoryTranslation}
                </span>
                <span className="text-neutral-darkest text-right text-base leading-none whitespace-nowrap">
                  <span data-testid={testIds.tickets.cartItemPrice}>{formatMoney(item.pricePerUnit)}</span> x{' '}
                  <span data-testid={testIds.tickets.cartItemQuantity}>{item.totalQuantity}</span>
                </span>
                <span
                  data-testid={testIds.tickets.cartItemSubtotal}
                  className="text-neutral-darkest text-right text-base leading-none whitespace-nowrap"
                >
                  {formatMoney(item.totalPrice)}
                </span>
              </Fragment>
            ))}
          </div>

          <div className="border-neutral-darkest w-full border-t-3 border-dotted" />

          <div className="flex items-center justify-between">
            <span className="text-neutral-darkest text-base leading-none font-semibold">
              {getTranslation('confirmationTotal')}:
            </span>
            <span
              data-testid={testIds.tickets.totalToPay}
              className="text-neutral-darkest text-right text-base leading-none font-semibold"
            >
              {formatMoney(totalPrice)}
            </span>
          </div>
        </>
      )}
    </div>
  )
}
